// Implements: F-UIX-007
/**
 * A chip that opens a panel (the mockup's topbar chips: scope, profile, versions; UX-05).
 *
 * The chip is a real button that names what it opens and what it shows ("Ámbito del mockup:
 * Valtx"), says it opens a dialog and whether it is open. The panel is a `Popover`: anchored below
 * the chip, Escape closes it with the focus back on the chip, a press outside closes it, a second
 * press on the chip closes it, and the preview frames are guarded while it is open.
 */

import { useId, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { Icon } from "../atoms/Icon";
import type { FloatingAlign } from "./Popover";
import { Popover } from "./Popover";
import styles from "./ChipPopover.module.css";

export interface ChipPopoverProps {
  /** What the chip shows (a scope's name, a profile's name, "v7"). */
  readonly label: string;
  /** The panel's heading, which names the dialog. */
  readonly title: string;
  /** What the chip is about, read before its label ("Ámbito del mockup"). */
  readonly description: string;
  /** Drawn before the label: a glyph or a colour swatch. */
  readonly leading?: ReactNode;
  readonly width?: "auto" | "sm" | "md" | "lg" | undefined;
  readonly align?: FloatingAlign | undefined;
  /** A line about what the chip shows, as its tooltip ("v7 (actual) · USD 0.12 · 3 min"). */
  readonly hint?: string | undefined;
  /** An id for the chip itself, so a page can give it the focus back. */
  readonly triggerId?: string | undefined;
  /** The panel's content; a function receives the way to close it. */
  readonly children: ReactNode | ((close: () => void) => ReactNode);
}

export function ChipPopover({
  label,
  title,
  description,
  leading,
  width = "md",
  align = "start",
  hint,
  triggerId,
  children,
}: ChipPopoverProps): ReactElement {
  const anchor = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const close = (): void => {
    setOpen(false);
  };
  return (
    <>
      <button
        ref={anchor}
        id={triggerId}
        type="button"
        className={styles.chip}
        aria-label={`${description}: ${label}`}
        title={hint}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {leading}
        <span className={styles.label}>{label}</span>
        <Icon name="chevron-down" size="sm" />
      </button>
      <Popover open={open} onClose={close} anchorRef={anchor} labelledBy={titleId} width={width} align={align}>
        <div className={styles.panel}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {typeof children === "function" ? children(close) : children}
        </div>
      </Popover>
    </>
  );
}
